import {
    defaultLanguage,
    defaultTimezone,
    lsLanguage,
} from './userProfileContextHelpers';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';

import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';

// Extiende dayjs con el plugin para formatos personalizados
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localeData);
dayjs.extend(relativeTime);

export const dateLanguageFormats = {
    en: 'MM/DD/YYYY',
    fr: 'DD/MM/YYYY',
    es: 'DD/MM/YYYY',
    de: 'DD/MM/YYYY',
};

export const shortMonthFormatByLanguage = {
    es: 'DD MMM',
    en: 'MMM DD',
    de: 'DD MMM',
    fr: 'DD MMM',
};

export const getLanguageFormat = lang => {
    let language;
    if (lang) {
        language = lang;
    } else if (lsLanguage) {
        //hay LS
        language = lsLanguage;
    } else {
        language = defaultLanguage;
    }
    return dateLanguageFormats[language];
};

export const formatDate = (date, lang) => {
    // es una fecha válida
    if (!date || (typeof date !== 'string' && !(date instanceof Date))) {
        return '--/--/--';
    }
    const parsedDate = new Date(date);
    if (
        isNaN(parsedDate) || // fecha inválida
        isMinValueDate(date) // fecha predeterminadas
    ) {
        return '--/--/--';
    }
    const languageFormat = getLanguageFormat(lang);
    return dayjs(date).format(languageFormat);
};

export const isMinValueDate = date => {
    if (!date) return false;
    return date === '0001-01-01T00:00:00';
};

export const formatDateYYMMDD = date => dayjs(date).format('YYYY/MM/DD');

export const formatRelativeTime = (date, lang = 'es') => {
    // returns a human-readable relative time string (e.g., "3 hours ago")

    if (!date) return '';
    return dayjs(date).locale(lang).fromNow();
};

export const formatUTCDateTime = (date, lang = 'es') => {
    const languageFormat = dateLanguageFormats[lang];
    const formatedDate = dayjs.utc(date).format(`${languageFormat} HH:mm:ss`);
    if (
        !date ||
        formatedDate === '01/01/0001' ||
        formatedDate === 'Invalid date'
    ) {
        return '--/--/--';
    } else {
        return formatedDate;
    }
};

export const getNextDay = date => {
    if (!date) return undefined;
    let currentDate = new Date(date);
    return currentDate.setDate(date.getDate() + 1);
};

export const getCurrentMonth = (date = new Date()) => {
    return dayjs(date).month();
};

export const getCurrentYear = (date = new Date()) => {
    return dayjs(date).year();
};

export const getYearPeriod = () => {
    //year ago = si estamos a 15 de mayo de 2023 => 01/06/2022
    const yearAgo = getYearAgoDate();
    // endThisMonth = si estamos a 15 de mayo de 2023 => 31/05/2023
    const endThisMonth = dayjs().endOf('month').format('YYYY-MM-DD');

    return { start: yearAgo, end: endThisMonth };
};

export const getMonthPeriod = (date = new Date()) => {
    //month ago = si estamos a 15 de mayo de 2023 => 15/04/2023

    const monthAgo = dayjs(date).subtract(1, 'months').format('YYYY-MM-DD');
    const endThisMonth = dayjs().endOf('month').format('YYYY-MM-DD');
    return { start: monthAgo, end: endThisMonth };
};

export const getYearAgoDate = () => {
    //Esta función devuelve la fecha del día uno del mes siguiente a la fecha de hace un año.
    const currentDate = dayjs();
    const yearAgo = currentDate.subtract(1, 'years');
    const firstOfNextMonth = yearAgo.add(1, 'months').startOf('month');
    return firstOfNextMonth.format('YYYY-MM-DD');
};

export const addMonthsToDate = (initialDate, numberOfMonths) => {
    return dayjs(initialDate)
        .add(numberOfMonths, 'months')
        .format('YYYY-MM-DD');
};

export const dateIsInRange = (stringDate, dateRange) => {
    const { start, end } = dateRange;
    return dayjs(stringDate).isBetween(start, end, null, '[)');
};

export const isPastDate = date => {
    return dayjs(date).isBefore(dayjs(), 'day');
};

export const formatDateTime = (date, lang, timeZone = defaultTimezone) => {
    if (!date) {
        return '--/--/--, --:--:--';
    }

    const formatLanguage = getLanguageFormat(lang);

    // Convertimos la fecha a UTC, la ajustamos a la zona horaria y la formateamos
    const dateUtc = dayjs.utc(date).tz(timeZone);
    return dateUtc.format(`${formatLanguage}, HH:mm:ss`);
};

export const formatDateCard = date => {
    return dayjs(date).format('MM/YYYY');
};

export const convertDateToUTC = date => {
    return `${dayjs(date).format('YYYY-MM-DD')}T00:00:00`;
};

export const formatDateToEndOfDayUTC = date => {
    const formattedDate = dayjs(date)
        .endOf('day')
        .utc()
        .format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]');
    return formattedDate;
};

export const convertDateToTimeZone = (date, timeZone) => {
    return dayjs(date).tz(timeZone).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
};

export const convertDateToEndOfTimeZone = (date, timeZone) => {
    return dayjs(date)
        .tz(timeZone)
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
};

export const getTodayDate = () => {
    return new Date().toISOString().substring(0, 19);
};

export const formatCreditCardExpireDate = date => {
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format('MM/YY') : '--/--';
};

export const categorizePeriod = (fromDate, toDate) => {
    //  return 'old' | 'current' | 'upcoming';
    const today = dayjs();

    const hasFrom = !isMinValueDate(fromDate);
    const hasTo = !isMinValueDate(toDate);

    const from = hasFrom ? dayjs(fromDate) : null;
    const to = hasTo ? dayjs(toDate) : null;

    // No hay fechas → current
    if (!hasFrom && !hasTo) return 'current';

    // Sólo fromDate existe
    if (hasFrom && !hasTo) {
        return today.isBefore(from, 'day') ? 'upcoming' : 'current';
    }

    // Solo toDate existe
    if (!hasFrom && hasTo) {
        return today.isAfter(to, 'day') ? 'old' : 'current';
    }

    // Ambas fechas existen
    if (from && to) {
        if (today.isBefore(from, 'day')) return 'upcoming';
        if (today.isAfter(to, 'day')) return 'old';
        return 'current';
    }

    return 'current'; // fallback
};

export const convertDateToDayjs = date => {
    return date ? dayjs(date) : null;
};

export const getPastDate = (amount, unit) => {
    // units: 'day' | 'month' | 'year'| "d"| "M"| "y"
    return dayjs().subtract(amount, unit).startOf('day').toISOString();
};

export const daysBetween = (from, to) => {
    const start = dayjs(from);
    const end = dayjs(to);
    return end.diff(start, 'day');
};

// Inicio de mes
export const startOfMonth = date => {
    return dayjs(date).startOf('month').format('YYYY-MM-DD');
};

export const startOfWeek = (date, firstDay) => {
    const d = dayjs(date);
    const day = d.day(); // 0 = domingo, 1 = lunes...
    const diff = (day - firstDay + 7) % 7;
    return d.subtract(diff, 'day').format('YYYY-MM-DD');
};

export const endOfWeek = (date, firstDay) => {
    const start = dayjs(startOfWeek(date, firstDay));
    return start.add(6, 'day').format('YYYY-MM-DD');
};

// Formato mes y año legible ("marzo 2026")
export const formatMonthYear = (date, lang) => {
    return dayjs(date).locale(lang).format('MMMM YYYY');
};

// Formato día y mes legible ("15 mar")
export const formatDayMonth = (date, lang) => {
    return dayjs(date).locale(lang).format('D MMM');
};

export const dateTenYearsAgo = convertDateToUTC(
    dayjs().subtract(10, 'year').format('YYYY-MM-DD')
);

export const dateInTenYears = convertDateToUTC(
    dayjs().add(10, 'year').format('YYYY-MM-DD')
);

export const isTodayInPeriod = (startDate, endDate) => {
    const today = dayjs();
    return today.isBetween(startDate, endDate, 'day', '[]');
};

export const isDateTenYearsAgo = date => {
    const today = dayjs();
    const targetDate = dayjs(date);
    return targetDate.diff(today, 'year') <= -10;
};

export const isDateInTenYearsAway = date => {
    const today = dayjs();
    const targetDate = dayjs(date);
    return targetDate.diff(today, 'year') >= 10;
};

export const addDaysToDate = (date, daysToAdd) => {
    return dayjs(date).add(daysToAdd, 'day').format('YYYY-MM-DD');
};

export const addOneDay = date => {
    return addDaysToDate(date, 1);
};

export const isDateless = date => {
    if (!date) return true;
    return (
        isMinValueDate(date) ||
        // sometimes we use these extreme dates to represent "no date", so we want to consider them as dateless
        isDateTenYearsAgo(date) ||
        isDateInTenYearsAway(date)
    );
};

export const formatShortDate = (date, lang) => {
    const format =
        shortMonthFormatByLanguage[lang] ||
        shortMonthFormatByLanguage[defaultLanguage];
    return dayjs(date).locale(lang.toLowerCase()).format(format).toLowerCase();
};
